// -*- coding: utf-8 -*-
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { apiUrl, fetchJson } from '../../apiBase';
import { listValuePrograms, saveValueProgram, deleteValueProgram, listThemes, getTheme } from '../api/values';

const typeOptions = [
  { value: 'overview', label: 'Genel Bakış' },
  { value: 'activity', label: 'Etkinlik' },
  { value: 'observation', label: 'Gözlem' },
];

const emptyProgram = {
  id: '',
  class_id: '',
  theme_id: '',
  week_start_date: '',
  timezone: 'Europe/Istanbul',
  enabled: true,
  schedule: [],
};

const ValuesScheduleManager = ({ schoolId, onError, onSuccess }) => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [themes, setThemes] = useState([]);
  const [editing, setEditing] = useState(emptyProgram);
  const [saving, setSaving] = useState(false);
  const [classes, setClasses] = useState([]);
  const [classesById, setClassesById] = useState({});
  const [themesById, setThemesById] = useState({});
  const [themeDays, setThemeDays] = useState([]); // mapped activities: {id, index(sort_order), title, type(activity_type)}
  const indexOptions = useMemo(() => {
    const opts = [{ value: 0, label: '0 - Genel Bakış' }];
    const sorted = [...themeDays].sort((a, b) => (Number(a.index || 0) - Number(b.index || 0)));
    sorted.forEach((d) => {
      const title = d.title || `Öğe ${d.index}`;
      opts.push({ value: Number(d.id || 0), label: `${d.index}-${title}` });
    });
    return opts;
  }, [themeDays]);
  const normalizeTime = (raw) => {
    const s = String(raw || '').trim();
    if (!s) return s;
    if (/^\d{1,2}$/.test(s)) {
      const hh = parseInt(s, 10);
      if (hh >= 0 && hh <= 23) return `${String(hh).padStart(2, '0')}:00`;
    }
    if (/^\d{3}$/.test(s)) {
      const hh = Math.floor(parseInt(s, 10) / 100);
      const mm = parseInt(s, 10) % 100;
      if (hh >= 0 && hh <= 23 && mm >= 0 && mm <= 59) return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
    }
    if (/^\d{4}$/.test(s)) {
      const hh = parseInt(s.slice(0, 2), 10);
      const mm = parseInt(s.slice(2), 10);
      if (hh >= 0 && hh <= 23 && mm >= 0 && mm <= 59) return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
    }
    return s;
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [progRes, themeRes, clsRes] = await Promise.all([
        listValuePrograms(schoolId),
        listThemes(schoolId),
        fetchJson(apiUrl(`/admin/classes/${encodeURIComponent(schoolId)}`)),
      ]);
      setPrograms(progRes.items || []);
      const themeItems = (themeRes.items || []).map((t) => ({ value: t.id, label: t.title || `Tema ${t.id}` }));
      setThemes(themeItems);
      setThemesById((themeRes.items || []).reduce((acc, t) => ({ ...acc, [t.id]: t }), {}));
      try {
        const cls = await clsRes.json();
        const mapped = (cls || []).map((c) => {
          if (c && typeof c === 'object') {
            const id = c.id;
            const name = c.name || c.code || String(c.id || '');
            return { value: String(id ?? ''), label: name };
          }
          return { value: String(c || ''), label: String(c || '') };
        });
        setClasses(mapped);
        setClassesById(
          (cls || []).reduce((acc, c) => {
            if (c && typeof c === 'object' && c.id != null) {
              acc[String(c.id)] = c.name || c.code || String(c.id);
            }
            return acc;
          }, {})
        );
      } catch (_) {
        setClasses([]);
        setClassesById({});
      }
    } catch (e) {
      onError?.('Programlar yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [schoolId, onError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEdit = (prog) => {
    setEditing({
      ...emptyProgram,
      ...prog,
      class_id: prog?.class_id != null ? String(prog.class_id) : '',
      schedule: (prog.schedule || []).map((s, idx) => ({
        activity_id: s.theme_activity_id || 0,
        type: s.schedule_type || 'activity',
        send_date: s.send_date || '',
        send_time: s.send_time || '',
      })),
    });
    if (prog.theme_id) {
      getTheme(schoolId, prog.theme_id)
        .then((data) => setThemeDays((data?.activities || []).map((a) => ({
          id: a.id,
          index: a.sort_order,
          title: a.title,
          type: a.activity_type,
        }))))
        .catch(() => setThemeDays([]));
    } else {
      setThemeDays([]);
    }
  };

  const handleNew = () => setEditing(emptyProgram);

  const updateSchedule = (idx, patch) => {
    setEditing((prev) => {
      const next = prev.schedule.map((s, i) => (i === idx ? { ...s, ...patch } : s));
      return { ...prev, schedule: next };
    });
  };

  const addScheduleItem = () => {
    setEditing((prev) => ({
      ...prev,
      schedule: [
        ...prev.schedule,
        { activity_id: 0, type: 'activity', send_date: '', send_time: '20:00' },
      ],
    }));
  };

  const removeScheduleItem = (idx) => {
    setEditing((prev) => ({
      ...prev,
      schedule: prev.schedule.filter((_, i) => i !== idx),
    }));
  };

  const handleSave = async () => {
    if (!editing.class_id || !editing.theme_id) {
      onError?.('Sınıf ve tema zorunludur.');
      return;
    }

    const classIdInt = parseInt(String(editing.class_id), 10);
    const themeIdInt = parseInt(String(editing.theme_id), 10);
    if (!Number.isFinite(classIdInt) || classIdInt <= 0) {
      onError?.('Geçerli bir sınıf seçin.');
      return;
    }
    if (!Number.isFinite(themeIdInt) || themeIdInt <= 0) {
      onError?.('Geçerli bir tema seçin.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        id: editing.id || undefined,
        class_id: classIdInt,
        theme_id: themeIdInt,
        timezone: editing.timezone || 'Europe/Istanbul',
        enabled: !!editing.enabled,
        schedule: (editing.schedule || []).map((s) => ({
          schedule_type: s.type || 'activity',
          send_date: s.send_date,
          send_time: normalizeTime(s.send_time),
          theme_activity_id: s.type === 'overview' ? null : (s.activity_id ? Number(s.activity_id) : null),
        })),
      };
      if (!payload.id) delete payload.id;
      const res = await saveValueProgram(schoolId, payload);
      onSuccess?.('Program kaydedildi.');
      await loadData();
      handleEdit(res.program);
    } catch (e) {
      onError?.('Program kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pid) => {
    try {
      await deleteValueProgram(schoolId, pid);
      onSuccess?.('Program silindi.');
      await loadData();
      if (editing.id === pid) handleNew();
    } catch (e) {
      onError?.('Program silinemedi.');
    }
  };

  const fillDates = (schedule, weekStart) => {
    if (!weekStart) return schedule;
    const base = new Date(weekStart);
    if (Number.isNaN(base.getTime())) return schedule;
    return (schedule || []).map((s, i) => {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
        return { ...s, send_date: d.toISOString().slice(0, 10), send_time: s.send_time || '20:00' };
    });
  };

  const buildScheduleFromTheme = (theme, weekStart) => {
    const days = [...(theme?.activities || [])].sort((a, b) => (Number(a.sort_order || 0) - Number(b.sort_order || 0)));
    const sched = [{ activity_id: 0, type: 'overview', send_date: '', send_time: '20:00' }];
    days.forEach((d) => {
      sched.push({
        activity_id: Number(d.id || 0),
        type: d.activity_type || 'activity',
        send_date: '',
        send_time: '20:00',
      });
    });
    return fillDates(sched, weekStart);
  };

  const handleWeekStartChange = (dateStr) => {
    setEditing((prev) => {
      if (!dateStr) return { ...prev, week_start_date: dateStr };
      const base = new Date(dateStr);
      if (Number.isNaN(base.getTime())) return { ...prev, week_start_date: dateStr };
      const updatedSchedule = fillDates(prev.schedule || [], dateStr);
      return { ...prev, week_start_date: dateStr, schedule: updatedSchedule };
    });
  };

  const handleThemeChange = async (val) => {
    setEditing((prev) => ({ ...prev, theme_id: val }));
    try {
      const data = await getTheme(schoolId, val);
      const sched = buildScheduleFromTheme(data, editing.week_start_date);
      setThemeDays((data?.activities || []).map((a) => ({
        id: a.id,
        index: a.sort_order,
        title: a.title,
        type: a.activity_type,
      })));
      setEditing((prev) => ({ ...prev, theme_id: val, schedule: sched }));
    } catch (_) {
      // ignore
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScheduleIcon fontSize="small" /> Programlar
            </Typography>
            <Stack direction="row" spacing={1}>
              <IconButton size="small" onClick={loadData} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={handleNew}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={1.5}>
            {programs.map((p) => (
              <Paper
                key={p.id}
                variant={editing.id === p.id ? 'outlined' : 'elevation'}
                sx={{
                  p: 1.5,
                  borderLeft: editing.id === p.id ? '4px solid' : '1px solid',
                  borderLeftColor: editing.id === p.id ? 'primary.main' : 'divider',
                  cursor: 'pointer',
                }}
                onClick={() => handleEdit(p)}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {(classesById?.[String(p.class_id)] || p.class_id)} • {themesById?.[p.theme_id]?.title || `Tema ${p.theme_id}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Başlangıç: {p.week_start_date || '-'}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                  <Chip size="small" label={p.enabled ? 'Aktif' : 'Pasif'} color={p.enabled ? 'success' : 'default'} />
                  <Chip size="small" label={p.timezone || 'TZ'} />
                </Stack>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  sx={{ mt: 1 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(p.id);
                  }}
                >
                  Sil
                </Button>
              </Paper>
            ))}
            {programs.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Henüz program yok. Yeni program ekleyin.
              </Typography>
            )}
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={12} md={8}>
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Program Düzenleyici</Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving || !editing.class_id || !editing.theme_id}
              >
                Kaydet
              </Button>
            </Stack>
          </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Sınıf"
                  value={editing.class_id}
                  onChange={(e) => setEditing((prev) => ({ ...prev, class_id: e.target.value }))}
                  fullWidth
                >
                  <MenuItem value="">
                    Seçin
                  </MenuItem>
                  {classes.map((c) => (
                    <MenuItem key={c.value} value={c.value}>
                      {c.label}
                    </MenuItem>
                  ))}
                </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Tema"
                value={editing.theme_id}
                onChange={(e) => handleThemeChange(e.target.value)}
                fullWidth
              >
                {themes.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Hafta Başlangıç Tarihi"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={editing.week_start_date}
                onChange={(e) => handleWeekStartChange(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Saat Dilimi"
                value={editing.timezone}
                onChange={(e) => setEditing((prev) => ({ ...prev, timezone: e.target.value }))}
                fullWidth
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle1">Zamanlama</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addScheduleItem}>
              Adım Ekle
            </Button>
          </Box>
          <Stack spacing={1.5}>
            {editing.schedule.map((s, idx) => (
              <Paper
                key={idx}
                variant="outlined"
                sx={{
                  p: { xs: 1.25, sm: 1.5 },
                  borderRadius: 2,
                  boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                  borderColor: 'divider',
                }}
              >
                <Stack spacing={1.25}>
                  <TextField
                    select
                    label="Öğe"
                    value={s.activity_id ?? 0}
                    onChange={(e) => {
                      const nextId = Number(e.target.value);
                      let nextType = s.type;
                      if (nextId === 0) {
                        nextType = 'overview';
                      } else {
                        const match = themeDays.find((d) => Number(d.id) === nextId);
                        if (match?.type) nextType = match.type;
                      }
                      updateSchedule(idx, { activity_id: nextId, type: nextType });
                    }}
                    fullWidth
                    size="small"
                    sx={{ backgroundColor: '#fafafa', borderRadius: 1 }}
                  >
                    {indexOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <TextField
                      label="Gönderim Tarihi"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={s.send_date}
                      onChange={(e) => updateSchedule(idx, { send_date: e.target.value })}
                      fullWidth
                      size="small"
                      sx={{ backgroundColor: '#fafafa', borderRadius: 1, flex: 1, minWidth: 160 }}
                    />
                    <TextField
                      label="Gönderim Saati (HH:MM)"
                      value={s.send_time}
                      onChange={(e) => updateSchedule(idx, { send_time: e.target.value })}
                      onBlur={(e) => updateSchedule(idx, { send_time: normalizeTime(e.target.value) || '20:00' })}
                      placeholder="20:00"
                      inputProps={{ step: 300, inputMode: 'numeric' }}
                      fullWidth
                      size="small"
                      sx={{ backgroundColor: '#fafafa', borderRadius: 1, flex: 1, minWidth: 140 }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Chip
                      label={typeOptions.find((t) => t.value === s.type)?.label || s.type}
                      color={s.type === 'observation' ? 'secondary' : 'default'}
                      variant="outlined"
                      sx={{ minWidth: 90, justifyContent: 'center' }}
                    />
                    <IconButton color="error" onClick={() => removeScheduleItem(idx)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Stack>
              </Paper>
            ))}
            {editing.schedule.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Zamanlama ekleyin.
              </Typography>
            )}
          </Stack>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default ValuesScheduleManager;
